import React, { useState } from "react";
import Head from 'next/head';
import { Avatar, Button, ConfigProvider, Drawer, Layout, Menu, MenuProps } from "antd";
import { faBars, faSignOut, faSignIn, faCubes, faUser, faUsers, faList } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRouter } from "next/router";
import { useSession, signIn, signOut } from "next-auth/react";
import { Image } from "antd";
import nProgress from "nprogress";
import { Authorize } from "./Authorize";

const { Content, Sider } = Layout;

const sidebarBackgroundColor = '#FFFFFF';
const sidebarMenuSelectedItemBackgroundColor = '#0B326F';

const DefaultLayout: React.FC<{
    children: React.ReactNode
}> = ({ children }) => {

    const [drawerOpen, setDrawerOpen] = useState(false);
    const router = useRouter();
    const { data: session, status } = useSession();

    // menu.key must match the router.pathname, see example below: "/dashboard"
    const [selected, setSelected] = useState([router.pathname]);
    // key must also be unique, for obvious reason
    function getMenu(): MenuProps['items'] {
        const menu: MenuProps['items'] = [];

        menu.push({
            key: '/users',
            label: 'Users',
            icon: <FontAwesomeIcon icon={faUsers}></FontAwesomeIcon>,
            onClick: () => router.push('/users')
        });

        menu.push({
            key: '/events',
            label: 'Events',
            icon: <FontAwesomeIcon icon={faCubes}></FontAwesomeIcon>,
            onClick: () => router.push('/events')
        });


        menu.push({
            key: '/report',
            label: 'Reports',
            icon: <FontAwesomeIcon icon={faList}></FontAwesomeIcon>,
            onClick: () => router.push('/report')
        });


        if (status === 'authenticated') {
                        menu.push({
                key: '/powered-by',
                disabled: true,
                style: {
                    position: "fixed",
                    bottom: 0,
                    width: 210,
                    paddingLeft: "20px",
                },
                label: (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 12, color: "#888" }}>
                            Powered by
                        </span>
                        <Image
                            src="/images/accara-logo.png"
                            alt="Accara Logo"
                            preview={false}
                            height={16}
                        />
                    </div>
                )
            });

            menu.push({
                key: '/sign-out',
                label: 'Sign out',
                style: {
                    position: "fixed",
                    bottom: 50,
                    width: 210,
                    paddingRight: "10px",
                    paddingLeft: "20px",
                    paddingBottom: "10px",
                    paddingTop: "10px",
                },
                icon: <FontAwesomeIcon icon={faSignOut}></FontAwesomeIcon>,
                onClick: () => {
                    nProgress.start();
                    // signOut();
                    // HINT: use this method call if need to end SSO server authentication session:
                    signOut({
                        callbackUrl: '/api/end-session'
                    });
                }
            });
        } else {
            menu.push({
                key: '/sign-in',
                label: 'Sign in',
                style: {
                    position: "fixed",
                    bottom: 0,
                    paddingRight: "10px",
                    paddingLeft: "20px",
                    paddingBottom: "10px"
                },
                icon: <FontAwesomeIcon icon={faSignIn}></FontAwesomeIcon>,
                onClick: () => {
                    nProgress.start();
                    signIn('oidc');
                }
            });
        }

        return menu;
    }

    const displayUserName = session?.user?.name;

    function renderAvatar() {
        if (status === 'authenticated') {
            return (
                <div className="flex mt-6 px-8 translate-y-6">
                    <div className="font-normal">
                        <Avatar size={45} icon={<FontAwesomeIcon icon={faUser}></FontAwesomeIcon>} />
                        <span className="ml-4">{displayUserName}</span>
                    </div>
                </div>
            );
        }

        return null;
    }

    return (
        <Authorize>
            <ConfigProvider theme={{
                components: {
                    Layout: {
                        // Sidebar background color:
                        // https://github.com/ant-design/ant-design/blob/5.0.0/components/layout/style/index.tsx#L101
                        colorBgHeader: sidebarBackgroundColor
                    }
                }
            }}>
                <Layout className="min-h-screen">
                    <Head>
                        <meta key="meta-charset" charSet="utf-8" />
                        <meta key="meta-viewport" name="viewport" content="width=device-width, initial-scale=1" />
                        <link key="favicon" rel="icon" href="/logo-mpk.ico" />
                    </Head>

                    <Sider width={240} className="fixed pb-24 pt-10 hidden lg:block">
                        <div className="flex justify-center">
                            <Image
                                src="/images/logo-mpk.png"
                                alt="Picture of the author"
                            />
                        </div>
                        <ConfigProvider theme={{
                            components: {
                                Menu: {
                                    // https://github.com/ant-design/ant-design/blob/5.0.0/components/menu/style/theme.tsx#L48
                                    colorItemBg: sidebarBackgroundColor,
                                    // https://github.com/ant-design/ant-design/blob/5.0.0/components/menu/style/theme.tsx#L133
                                    colorItemBgSelected: sidebarMenuSelectedItemBackgroundColor,
                                }
                            }
                        }}>
                            <Menu theme="light" mode="inline" items={getMenu()} className="p-3 font-bold"
                                defaultOpenKeys={[`/${selected[0]?.split('/')[1]}`]}
                                defaultSelectedKeys={selected}
                                selectedKeys={selected} onSelect={e => setSelected(e.selectedKeys)} />
                        </ConfigProvider>
                    </Sider>
                    <Drawer placement="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                        <ConfigProvider theme={{
                            components: {
                                Menu: {
                                    // https://github.com/ant-design/ant-design/blob/5.0.0/components/menu/style/theme.tsx#L194
                                    colorActiveBarBorderSize: 0
                                }
                            }
                        }}>
                            <Menu mode="inline" items={getMenu()}
                                selectedKeys={selected} onSelect={e => setSelected(e.selectedKeys)} />
                        </ConfigProvider>
                    </Drawer>
                    <Layout style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
                        <div className='bg-topbar grid grid-cols-3 lg:hidden px-8 py-4 items-center'>
                            <div>
                                <Button onClick={() => setDrawerOpen(true)} type="primary">
                                    <FontAwesomeIcon icon={faBars}></FontAwesomeIcon>
                                </Button>
                            </div>
                            <div className="h-12 p-2 text-white bg-slate-600">
                                Logo
                            </div>
                            <div></div>
                        </div>
                        <div className="flex justify-end m-5 z-10">
                            {renderAvatar()}
                        </div>
                        <div className="-translate-y-20">
                            <Content className="p-8 bg-stone-100">
                                {children}
                            </Content>
                        </div>
                    </Layout>
                </Layout>
            </ConfigProvider>
        </Authorize>
    );
}

export const WithDefaultLayout = (page: React.ReactElement) => <DefaultLayout>{page}</DefaultLayout>;
